import React from 'react';
import PropTypes from 'prop-types';
import TransitionGroup from 'react-transition-group/TransitionGroup';
import CSSTransition from 'react-transition-group/CSSTransition';
import styled from 'styled-components/macro';
import { connect } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { NoAuthNavBar } from 'stash/components/global/NoAuthNavBar';
import { flushSignUp } from 'stash/actions';

import SignUpScreens from './SignUpScreens';
import SignUpGraph from './SignUpGraph';
import SignUpFlow from './SignUpFlow';
import {
	getUser,
	getAccountBankAccounts,
	getInvestorApplication,
	getAccountAutoStash,
	getFundingSources,
	getActivePlatformTier,
} from 'stash/actions/api';
import { trackEvent } from 'stash/actions';
import { selectPlatformTierProductTypes } from 'stash/selectors';
import { IRA } from 'stash/constants/platformTierTypes';
import { ARCHIVE_RESTRICTED_REASONS } from 'stash/constants/mappings';
import { hasClosedAllAccounts } from 'stash/utils/helpers';
import { isProdOrStaging } from 'stash/utils';
import { selectSignUpState } from 'stash/selectors/signup';
import { EntitlementsProvider } from 'stash/hooks/useEntitlements';

import './SignUpApp.css';

const DagTransitionGroup = styled(TransitionGroup)`
	width: 100%;
	position: relative;
`;

export const mapStateToProps = (state, ownProps) => {
	const activeTierProductTypes = selectPlatformTierProductTypes(state);
	return {
		hasRetireProductType: activeTierProductTypes.indexOf(IRA) > -1,
		accessToken: state.entities.api_key.access_token,
		web_reg_version: state.entities.user.web_reg_version,
		steps: state.application.signUp,
		accounts: state.entities.account_by_type,
		store: selectSignUpState(state),
	};
};

export const mapDispatchToProps = (dispatch, ownProps) => ({
	getUser: () => {
		return dispatch(getUser());
	},
	getEverything: () => {
		return Promise.all([
			dispatch(getInvestorApplication()),
			dispatch(getActivePlatformTier()),
		]);
	},
	getAccountInfo: (accountId, accountType) => {
		// There's a race condition with account cards and auto-stash.
		return Promise.all([
			dispatch(getFundingSources()),
			dispatch(getAccountAutoStash(accountId, accountType)),
			dispatch(getAccountBankAccounts(accountId)),
		]);
	},
	flushSignUp: () => {
		dispatch(flushSignUp());
	},
	fireEvent: (eventName, properties) => {
		return dispatch(trackEvent(eventName, properties));
	},
});

export class SignUpApp extends React.Component {
	static propTypes = {
		preload: PropTypes.bool,
	};

	constructor(props) {
		super(props);

		this.state = {
			ready: false,
			currentStep: null,
		};

		this.product = props.match?.params?.product;
		this.signUpPrefix = `/sign-up-${this.product}`;
		this.graph = null;
		this.flow = null;

		this.setUpGraph = this.setUpGraph.bind(this);
		this.getNextSignUpStep = this.getNextSignUpStep.bind(this);
		this.navigateToNextStep = this.navigateToNextStep.bind(this);
		this.renderRoutes = this.renderRoutes.bind(this);

		this.setUpGraph();
	}

	setUpGraph() {
		this.flow = SignUpFlow.getFlow(this.product);

		if (!this.flow) {
			return (window.href = '/');
		}

		const { steps, transitions } = this.flow;

		this.graph = new SignUpGraph(steps, transitions, {
			quiet: isProdOrStaging(), // suppress graph errors on prod
			cyclic: isProdOrStaging(), // suppress graph cycle checks on prod
		});
	}

	getNextSignUpStep() {
		const currentStep = this.graph.getNextNode(this.props.store);
		this.setState({ currentStep });
		return SignUpScreens[currentStep];
	}

	navigateToNextStep(options = {}) {
		const { history } = this.props;
		const { canReturn, skipAnimation } = options;

		// Wait for all redux actions in callstack to complete.
		window.setTimeout(() => {
			const nextStep = this.getNextSignUpStep();
			const navigate = canReturn === false ? history.replace : history.push;

			const nextPath = `${this.signUpPrefix}/${nextStep.path}`;

			if (nextPath === this.props.location.pathname) return;

			navigate(nextPath, { skipAnimation });

			// Scroll back up for small-screen mobile devices.
			window.scrollY && window.scrollTo && window.scrollTo(0, 0);
		}, 0);
	}

	componentDidMount(prevProps, prevState) {
		const { getUser, getEverything, getAccountInfo, accessToken, history } = this.props;

		const nextStepNavigationOptions = { canReturn: false, skipAnimation: true };

		if (!accessToken) {
			return history.push('/log-in?r=%2Fsign-up');
		}

		getUser()
			.then((res) => {
				// redirect closed/restricted accounts
				if (
					hasClosedAllAccounts(res.accounts) ||
					ARCHIVE_RESTRICTED_REASONS[res.user.restricted_reason]
				) {
					return this.props.history.push('/archive/documents');
				}

				// Load account specific data if needed. We can assume we know the
				// sign up path they are on if they either have an account or a
				// web_reg_version.
				return getEverything()
					.then((responses) => {
						if (res.accounts.length || res.user.web_reg_version) {
							return Promise.all(
								res.accounts.map((account) => {
									return getAccountInfo(account.id, account.type);
								})
							).then(() => {
								this.setState({ ready: true });
							});
						} else {
							// Run after initial sign up flow redirect
							setTimeout(() => {
								this.setState({ ready: true });
								return this.props.history.push('/sign-up/finish-registration');
							}, 0);
						}
					})
					.catch((err) => err);
			})
			.then(() => {
				this.navigateToNextStep(nextStepNavigationOptions);
			})
			.catch((err) => err);
	}

	componentWillUnmount() {
		this.props.flushSignUp();
	}

	renderRoutes() {
		const routes = Object.values(SignUpScreens).map((screen) => {
			return (
				<Route
					key={screen.path}
					path={`/sign-up*/${screen.path}`}
					render={(props) => (
						<screen.component
							{...props}
							completeStep={this.navigateToNextStep}
							screen={screen}
							product={this.product}
						/>
					)}
				/>
			);
		});
		return routes;
	}

	renderSignUpContainer() {
		const { location } = this.props;

		const shouldSkipAnimation =
			this.props.location.state && this.props.location.state.skipAnimation;

		return (
			<DagTransitionGroup enter={!shouldSkipAnimation} exit={!shouldSkipAnimation}>
				<EntitlementsProvider>
					<CSSTransition
						key={location.key}
						classNames="sign-up-view-transition"
						timeout={{ enter: 300, exit: 300 }}
					>
						<section className="sign-up-form">
							<Switch location={location}>{this.renderRoutes()}</Switch>
						</section>
					</CSSTransition>
				</EntitlementsProvider>
			</DagTransitionGroup>
		);
	}

	render() {
		const { ready } = this.state;

		return (
			<div className="signup">
				<NoAuthNavBar />
				<main>{ready ? this.renderSignUpContainer() : <div />}</main>
			</div>
		);
	}
}

export default connect(mapStateToProps, mapDispatchToProps)(SignUpApp);
